// src/context/RecruitmentContext.jsx
import React, { createContext, useContext } from "react";
import {
  useJobOpenings,
  useCandidates,
  useRecruitmentRequests,
  useInterviews,
  useOffers,
  useRecruitmentActivity,
  useReferrals,
} from "../hooks/useRecruitment";

const RecruitmentContext = createContext(null);

export function useRecruitmentContext() {
  const ctx = useContext(RecruitmentContext);
  if (!ctx) throw new Error("useRecruitmentContext must be used within a RecruitmentProvider");
  return ctx;
}

export const PIPELINE_STAGES = [
  "Applied",
  "Screening",
  "Assessment",
  "Interview",
  "Manager Review",
  "HR Review",
  "Offer",
  "Background Verification",
  "Hired",
];

export const STAGE_COLORS = {
  Applied: "#6366f1",
  Screening: "#f59e0b",
  Assessment: "#8b5cf6",
  Interview: "#0ea5e9",
  "Manager Review": "#ec4899",
  "HR Review": "#14b8a6",
  Offer: "#10b981",
  "Background Verification": "#f97316",
  Hired: "#1b4b31",
  Rejected: "#ef4444",
  Withdrawn: "#6b7280",
  Hold: "#a855f7",
};

export function RecruitmentProvider({ children }) {
  const { data: jobOpenings, loading: jobsLoading } = useJobOpenings();
  const { data: candidates, loading: candidatesLoading } = useCandidates();
  const { data: recruitmentRequests, loading: requestsLoading } = useRecruitmentRequests();
  const { data: interviews, loading: interviewsLoading } = useInterviews();
  const { data: offers, loading: offersLoading } = useOffers();
  const { data: activityLogs, loading: activityLoading } = useRecruitmentActivity();
  const { data: referrals, loading: referralsLoading } = useReferrals();

  const value = {
    jobOpenings,
    jobsLoading,
    candidates,
    candidatesLoading,
    recruitmentRequests,
    requestsLoading,
    interviews,
    interviewsLoading,
    offers,
    offersLoading,
    activityLogs,
    activityLoading,
    referrals,
    referralsLoading,
    stages: PIPELINE_STAGES,
    stageColors: STAGE_COLORS,
  };

  return (
    <RecruitmentContext.Provider value={value}>
      {children}
    </RecruitmentContext.Provider>
  );
}
